/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { useIsMutating } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { TriggerDeviceButton } from "../../../Devices/TriggerDeviceButton";
import { Button } from "../../../Helper/Button";
import { useQcEntities } from "../QcEntityContext";
import useCheckFlowNavigate from "../useCheckFlowNavigate";
import { InvalidFruitModal } from "./InvalidFruitModal";

export function DeviceResultsNavButtons({ avocado, action }) {
    const config = useConfig();
    const { layer, check } = useQcEntities();
    const checkIsUpdating = useIsMutating() > 0;
    // No mutation dispatches avocados.isUpdating/isLoading anymore; a pending fruit mutation is the equivalent signal.
    const avocadoIsUpdating = useIsMutating() > 0;
    const params = useParams();
    const next = config.next_check_page(layer, check, params.action);
    const prev = config.prev_check_page(layer, check, params.action);
    const [isInvalidModalOpen, toggleInvalidModal] = useState(false);
    const { navigateToCheckAction } = useCheckFlowNavigate();
    const canNotContinue = check.avocados.length === 0 || avocadoIsUpdating || checkIsUpdating;
    const check_config = config.get_check_type_config(layer, check);
    const required_sample_size = check_config.pressure_calibration_settings?.required_sample_size || 0;

    // if avocado is removed/invalidated navigate to latest fruit
    useEffect(() => {
        if (avocado.avocado_id === null && check.avocados.length > 0) {
            const { avocado_id } = check.avocados[check.avocados.length - 1];

            navigateToCheckAction(`${action}/${avocado_id}`, true, {}, true);
        }
    }, [avocado.avocado_id, check.avocados.length]);

    return (
        <div className="py-4 d-md-flex justify-content-end align-items-center">
            {check.avocados.length > 0
                && <div className="me-auto pe-2">
                    {avocado.avocado_id > 0 && <span css={css`border-right: 1px solid #ddd;`} className="me-3 pe-3 d-inline-block">
                        <Button color="danger" className="py-1 my-1" outline={true} onClick={() => toggleInvalidModal(true)} icon={faTrash} />
                    </span>
                    }
                    <InvalidFruitModal isOpen={isInvalidModalOpen} index={check.avocados.findIndex((i) => i.avocado_id === avocado.avocado_id)} toggleModal={toggleInvalidModal} avocado={avocado} />
                    {check.avocados.map((a, index) => (
                        <Button key={a.avocado_id}
                            color={avocado.avocado_id === a.avocado_id ? "primary" : "secondary"}
                            className="me-2 py-1 my-1"
                            onClick={ () => navigateToCheckAction(`${action}/${a.avocado_id}`)}>{index + 1}
                        </Button>
                    ))}
                    { (required_sample_size > check.avocados.length) && [...Array(Math.max(required_sample_size - check.avocados.length, 0)).keys()].map((index) => <Button key={index} color="secondary" css={css`opacity: 0.3!important;`} disabled className="me-2 py-1 my-1" outline={true} >{check.avocados.length + index + 1}</Button>)}
                </div>
            }
            {prev
                && <div className="pb-2 pb-sm-0 me-2 d-inline-flex d-md-block">
                    <Button color="secondary" outline={true} disabled={canNotContinue} onClick={() => navigateToCheckAction(prev.path)}>Back</Button>
                </div>
            }
            <div className="me-2 d-inline-flex d-md-block">
                <div className="my-1">
                    <TriggerDeviceButton />
                </div>
            </div>
            {!next
                && <div className="me-2 d-inline-flex d-md-block">
                    <Button color="light" className="text-nowrap my-1" disabled={canNotContinue} block onClick={() => navigateToCheckAction(config.summary)} >Summary <span className="ms-1 badge badge-light">{check.avocados.length}</span></Button>
                </div>
            }
            {next
                && <div className="me-2 d-inline-flex d-md-block">
                    <Button color="light" className="text-nowrap my-1" disabled={canNotContinue} block onClick={() => navigateToCheckAction(next.path)} >Complete Scanning</Button>
                </div>
            }

        </div>
    );
}
DeviceResultsNavButtons.propTypes = {
    avocado: PropTypes.object,
    action: PropTypes.string
};
